/**
 * Budgets Page
 * Budget management and tracking for construction projects
 */

import React, { useState, useMemo } from 'react';
import { DollarSign, Plus, Download, ArrowRightLeft, Layers } from 'lucide-react';
import { ModuleLayout, EmptyState } from '../components/ui';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  BudgetHeader,
  BudgetTable,
  BudgetFilters,
  BudgetChart,
  BudgetLineItemDrawer,
  BudgetTransferDrawer,
  ForecastDrawer,
} from '../components/budgets';
import {
  useGetBudgetQuery,
  useGetLineItemsQuery,
  useCreateLineItemMutation,
  useUpdateLineItemMutation,
  useCreateTransferMutation,
  useUpdateForecastMutation,
  useExportBudgetMutation,
} from '../services/budgetApi';
import type {
  BudgetLineItem,
  BudgetGroupBy,
  BudgetFilterParams,
  CreateLineItemRequest,
  UpdateLineItemRequest,
  CreateTransferRequest,
  UpdateForecastRequest,
} from '../types/budget';

const Budgets: React.FC = () => {
  const projectId = useCurrentProjectId();

  // State
  const [groupBy, setGroupBy] = useState<BudgetGroupBy>('category');
  const [filters, setFilters] = useState<BudgetFilterParams>({
    showZeroBudget: false,
  });
  const [lineItemDrawerOpen, setLineItemDrawerOpen] = useState(false);
  const [transferDrawerOpen, setTransferDrawerOpen] = useState(false);
  const [forecastDrawerOpen, setForecastDrawerOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState<BudgetLineItem | null>(null);

  // API Queries
  const {
    data: budgetData,
    isLoading: budgetLoading,
    error: budgetError,
  } = useGetBudgetQuery({ projectId: projectId! }, { skip: !projectId });

  const {
    data: lineItemsData,
    isLoading: lineItemsLoading,
    refetch: refetchLineItems,
  } = useGetLineItemsQuery(
    {
      projectId: projectId!,
      budgetId: budgetData?.data?._id || '',
      filters,
    },
    { skip: !projectId || !budgetData?.data?._id }
  );

  // Mutations
  const [createLineItem, { isLoading: createLineItemLoading }] = useCreateLineItemMutation();
  const [updateLineItem, { isLoading: updateLineItemLoading }] = useUpdateLineItemMutation();
  const [createTransfer, { isLoading: createTransferLoading }] = useCreateTransferMutation();
  const [updateForecast, { isLoading: updateForecastLoading }] = useUpdateForecastMutation();
  const [exportBudget] = useExportBudgetMutation();

  const budget = budgetData?.data;
  const lineItems = lineItemsData?.data || [];
  const isLoading = budgetLoading || lineItemsLoading;

  // Extract unique categories and divisions for filters
  const { categories, divisions } = useMemo(() => {
    const cats = new Set<string>();
    const divs = new Set<string>();

    lineItems.forEach(item => {
      if (item.category) cats.add(item.category);
      if (item.division) divs.add(item.division);
    });

    return {
      categories: Array.from(cats).sort(),
      divisions: Array.from(divs).sort(),
    };
  }, [lineItems]);

  // Filter line items
  const filteredLineItems = useMemo(() => {
    return lineItems.filter(item => {
      // Filter by category
      if (filters.category && item.category !== filters.category) {
        return false;
      }

      // Filter by division
      if (filters.division && item.division !== filters.division) {
        return false;
      }

      // Filter by search
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesCostCode = item.costCode.toLowerCase().includes(searchLower);
        const matchesDescription = item.description.toLowerCase().includes(searchLower);
        if (!matchesCostCode && !matchesDescription) {
          return false;
        }
      }

      // Filter zero budget items
      if (!filters.showZeroBudget && item.originalBudget === 0) {
        return false;
      }

      return true;
    });
  }, [lineItems, filters]);

  // Handlers
  const handleAddLineItem = () => {
    setSelectedLineItem(null);
    setLineItemDrawerOpen(true);
  };

  const handleEditLineItem = (lineItem: BudgetLineItem) => {
    setSelectedLineItem(lineItem);
    setLineItemDrawerOpen(true);
  };

  const handleForecastLineItem = (lineItem: BudgetLineItem) => {
    setSelectedLineItem(lineItem);
    setForecastDrawerOpen(true);
  };

  const handleLineItemSubmit = async (data: CreateLineItemRequest | UpdateLineItemRequest) => {
    if (!projectId || !budget) return;

    try {
      if (selectedLineItem) {
        // Update existing
        await updateLineItem({
          projectId,
          budgetId: budget._id,
          lineItemId: selectedLineItem._id,
          data: data as UpdateLineItemRequest,
        }).unwrap();
      } else {
        // Create new
        await createLineItem({
          projectId,
          budgetId: budget._id,
          data: data as CreateLineItemRequest,
        }).unwrap();
      }
      await refetchLineItems();
      setLineItemDrawerOpen(false);
      setSelectedLineItem(null);
    } catch (error) {
      console.error('Error saving line item:', error);
    }
  };

  const handleTransferSubmit = async (data: CreateTransferRequest) => {
    if (!projectId || !budget) return;

    try {
      await createTransfer({
        projectId,
        budgetId: budget._id,
        data,
      }).unwrap();
      await refetchLineItems();
      setTransferDrawerOpen(false);
    } catch (error) {
      console.error('Error creating transfer:', error);
    }
  };

  const handleForecastSubmit = async (data: UpdateForecastRequest) => {
    if (!projectId || !budget) return;

    try {
      await updateForecast({
        projectId,
        budgetId: budget._id,
        data,
      }).unwrap();
      await refetchLineItems();
      setForecastDrawerOpen(false);
      setSelectedLineItem(null);
    } catch (error) {
      console.error('Error updating forecast:', error);
    }
  };

  const handleExport = async () => {
    if (!projectId || !budget) return;

    try {
      const blob = await exportBudget({
        projectId,
        data: {
          budgetId: budget._id,
          format: 'excel',
          includeLineItems: true,
          includeTransfers: true,
        },
      }).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget-${budget.name}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting budget:', error);
    }
  };

  // If no project selected
  if (!projectId) {
    return (
      <ModuleLayout
        title="All Budgets"
        description="Manage budgets and financial performance across all projects"
        icon={<DollarSign className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={DollarSign}
          title="No project selected"
          description="Please select a project from the header to view and manage its budget."
        />
      </ModuleLayout>
    );
  }

  // If budget doesn't exist
  if (!isLoading && !budget && !budgetError) {
    return (
      <ModuleLayout
        title="Project Budget"
        description="Track budget, costs, and financial performance for this project"
        icon={<DollarSign className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={DollarSign}
          title="No budget found"
          description="This project doesn't have a budget yet. Create one to start tracking costs and financial performance."
          action={{
            label: 'Create Budget',
            onClick: () => {
              // TODO: Open create budget modal
              console.log('Create budget');
            },
          }}
        />
      </ModuleLayout>
    );
  }

  // Error state
  if (budgetError) {
    return (
      <ModuleLayout
        title="Project Budget"
        description="Track budget, costs, and financial performance for this project"
        icon={<DollarSign className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={DollarSign}
          title="Error loading budget"
          description="There was an error loading the budget data. Please try again."
        />
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout
      title={budget?.name || 'Project Budget'}
      description={budget?.description || 'Track budget, costs, and financial performance'}
      icon={<DollarSign className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'Add Line Item',
        onClick: handleAddLineItem,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={[]}
    >
      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-3 mb-4">
        <button
          onClick={() => setTransferDrawerOpen(true)}
          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors inline-flex items-center"
        >
          <ArrowRightLeft className="h-4 w-4 mr-2" />
          Transfer Budget
        </button>
        <button
          onClick={handleExport}
          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors inline-flex items-center"
        >
          <Download className="h-4 w-4 mr-2" />
          Export
        </button>
      </div>

      {/* Budget Summary Header */}
      {budget && (
        <BudgetHeader
          revisedBudget={budget.totalRevisedBudget}
          committed={budget.totalCommitted}
          actual={budget.totalActual}
          variance={budget.totalVariance}
          variancePercent={budget.totalVariancePercent}
          health={budget.health}
          isLoading={isLoading}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Budget Table */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filters and Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-white border border-gray-200 rounded-lg p-2">
                <Layers className="h-4 w-4 text-gray-400" />
                <select
                  value={groupBy}
                  onChange={e => setGroupBy(e.target.value as BudgetGroupBy)}
                  className="text-sm border-0 focus:ring-0 pr-8"
                >
                  <option value="none">No Grouping</option>
                  <option value="category">Group by Category</option>
                  <option value="division">Group by Division</option>
                </select>
              </div>
            </div>
          </div>

          {/* Filters */}
          <BudgetFilters
            filters={filters}
            onFilterChange={setFilters}
            categories={categories}
            divisions={divisions}
          />

          {/* Budget Table */}
          <BudgetTable
            lineItems={filteredLineItems}
            groupBy={groupBy}
            onEdit={handleEditLineItem}
            onForecast={handleForecastLineItem}
            isLoading={isLoading}
          />
        </div>

        {/* Sidebar - Chart */}
        <div className="lg:col-span-1">
          {budget && (
            <BudgetChart
              data={{
                original: budget.totalOriginalBudget,
                changes: budget.totalApprovedChanges,
                committed: budget.totalCommitted,
                actual: budget.totalActual,
                forecast: budget.totalForecast,
              }}
            />
          )}
        </div>
      </div>

      {/* Drawers */}
      <BudgetLineItemDrawer
        isOpen={lineItemDrawerOpen}
        onClose={() => {
          setLineItemDrawerOpen(false);
          setSelectedLineItem(null);
        }}
        onSubmit={handleLineItemSubmit}
        lineItem={selectedLineItem}
        isLoading={createLineItemLoading || updateLineItemLoading}
        categories={categories}
        divisions={divisions}
      />

      <BudgetTransferDrawer
        isOpen={transferDrawerOpen}
        onClose={() => setTransferDrawerOpen(false)}
        onSubmit={handleTransferSubmit}
        lineItems={lineItems}
        isLoading={createTransferLoading}
      />

      <ForecastDrawer
        isOpen={forecastDrawerOpen}
        onClose={() => {
          setForecastDrawerOpen(false);
          setSelectedLineItem(null);
        }}
        onSubmit={handleForecastSubmit}
        lineItem={selectedLineItem}
        isLoading={updateForecastLoading}
      />
    </ModuleLayout>
  );
};

export default Budgets;
